import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  CalendarClock,
  Loader2,
  Plus,
  Pencil,
  Trash2,
  Clock,
} from "lucide-react";
import { toast } from "sonner";
import BackButton from "components/ui/back-button";
import { Button } from "components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "components/ui/dialog";
import { Input } from "components/ui/input";
import { Toaster } from "components/ui/sonner";
import {
  createAppointmentCategory,
  deleteAppointmentCategory,
  listAppointmentCategories,
  updateAppointmentCategory,
} from "../InboundCalls/inboundCallsApi";

interface AppointmentCategory {
  id: string;
  name: string;
  description: string;
  duration_minutes: number;
}

const DURATION_OPTIONS = [
  { label: "15 minutes", value: 15 },
  { label: "20 minutes", value: 20 },
  { label: "30 minutes", value: 30 },
  { label: "45 minutes", value: 45 },
  { label: "1 hour", value: 60 },
  { label: "1.5 hours", value: 90 },
  { label: "2 hours", value: 120 },
];

function formatDuration(minutes: number): string {
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const remaining = minutes % 60;
  if (remaining === 0) return `${hours} hr`;
  return `${hours} hr ${remaining} min`;
}

function AppointmentCategoriesPage() {
  const navigate = useNavigate();
  const [appointmentCategories, setAppointmentCategories] = useState<
    AppointmentCategory[] | null
  >(null);

  useEffect(() => {
    let cancelled = false;
    listAppointmentCategories()
      .then((loaded) => {
        if (!cancelled) {
          setAppointmentCategories(loaded);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setAppointmentCategories([]);
          toast.error("Could not load appointment categories.");
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const loading = appointmentCategories === null;

  // Dialog state
  const [showDialog, setShowDialog] = useState(false);
  const [editingCategory, setEditingCategory] = useState<AppointmentCategory | null>(null);
  const [saving, setSaving] = useState(false);

  // Form fields
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [durationMinutes, setDurationMinutes] = useState<number>(30);

  // Delete dialog
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState<AppointmentCategory | null>(
    null
  );
  const [deleting, setDeleting] = useState(false);

  const handleAdd = () => {
    setEditingCategory(null);
    setName("");
    setDescription("");
    setDurationMinutes(30);
    setShowDialog(true);
  };

  const handleEdit = (category: AppointmentCategory) => {
    setEditingCategory(category);
    setName(category.name);
    setDescription(category.description);
    setDurationMinutes(category.duration_minutes);
    setShowDialog(true);
  };

  const handleDeleteClick = (category: AppointmentCategory) => {
    setCategoryToDelete(category);
    setShowDeleteDialog(true);
  };

  const handleSave = async () => {
    if (!name.trim()) {
      toast.error("Please enter an appointment category name.");
      return;
    }
    if (!description.trim()) {
      toast.error("Please enter a description.");
      return;
    }

    setSaving(true);
    try {
      if (editingCategory) {
        const updated = await updateAppointmentCategory({
          id: editingCategory.id,
          name: name.trim(),
          description: description.trim(),
          duration_minutes: durationMinutes,
        });
        setAppointmentCategories((prev) =>
          (prev ?? []).map((category) =>
            category.id === updated.id ? updated : category
          )
        );
        toast.success("Appointment category updated!");
      } else {
        const created = await createAppointmentCategory({
          name: name.trim(),
          description: description.trim(),
          duration_minutes: durationMinutes,
        });
        setAppointmentCategories((prev) => [...(prev ?? []), created]);
        toast.success("Appointment category created!");
      }
    } catch {
      toast.error("Could not save that appointment category.");
      setSaving(false);
      return;
    }

    setSaving(false);
    setShowDialog(false);
    setEditingCategory(null);
    setName("");
    setDescription("");
    setDurationMinutes(30);
  };

  const handleDelete = async () => {
    if (!categoryToDelete) return;

    setDeleting(true);
    try {
      await deleteAppointmentCategory(categoryToDelete.id);
      setAppointmentCategories((prev) =>
        (prev ?? []).filter((category) => category.id !== categoryToDelete.id)
      );
      toast.success("Appointment category deleted!");
    } catch {
      toast.error("Could not delete that appointment category.");
    }
    setDeleting(false);
    setShowDeleteDialog(false);
    setCategoryToDelete(null);
  };

  return (
    <div className="w-full bg-background">
      <Toaster richColors position="top-center" />
      <div className="border-b-2 border-gray-100 px-4 py-2 sticky top-0 bg-white z-10 flex items-center justify-between gap-2">
        <BackButton onClick={() => navigate("/dashboard/inbound_calls")} />
      </div>
      <div className="p-4 space-y-4 max-w-4xl">
        {/* Page Header */}
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <h2 className="text-lg font-semibold tracking-tight flex items-center gap-2">
              <CalendarClock className="h-5 w-5 text-gray-500" />
              Appointment Categories
            </h2>
            <p className="text-xs text-gray-500 mt-1">
              Define appointment categories so the AI knows what to schedule and
              how long each visit takes
            </p>
          </div>
          <Button
            onClick={handleAdd}
            size="sm"
            className="h-8 text-xs bg-brand hover:bg-brand-hover text-white"
          >
            <Plus className="mr-2 h-3.5 w-3.5" />
            Add Category
          </Button>
        </div>

        {/* Info Banner */}
        <div className="rounded-lg border border-blue-200 bg-blue-50 p-3">
          <p className="text-xs text-blue-800">
            <strong>How it works:</strong> When a patient calls in, the AI
            identifies the type of appointment they need and uses the duration
            to find the next available time slot of that length in the provider's calendar
            (e.g., a 20-minute appointment could be 10:05am-10:25am or 10:19am-10:39am based on availability).
          </p>
        </div>

        {/* Appointment Categories List */}
        {loading ? (
          <Card className="gap-2 py-4">
            <CardContent className="px-4 flex items-center justify-center py-12">
              <Loader2 className="h-5 w-5 animate-spin text-brand mr-2" />
              <span className="text-gray-600 text-sm">
                Loading appointment categories...
              </span>
            </CardContent>
          </Card>
        ) : (
          <Card className="gap-2 py-4">
            <CardHeader className="px-4 pb-2">
              <CardTitle className="text-sm font-semibold text-gray-900">
                Your Appointment Categories
              </CardTitle>
              <CardDescription className="text-xs">
                Each category defines a name, description, and duration for the AI
                to reference when scheduling
              </CardDescription>
            </CardHeader>
            <CardContent className="px-4">
              {appointmentCategories.length === 0 ? (
                <div className="text-center py-10">
                  <div className="p-3 bg-gray-100 rounded-full w-12 h-12 mx-auto mb-3 flex items-center justify-center">
                    <CalendarClock className="h-6 w-6 text-gray-400" />
                  </div>
                  <h3 className="text-sm font-semibold text-gray-900 mb-1">
                    No appointment categories yet
                  </h3>
                  <p className="text-xs text-gray-600 mb-4 max-w-sm mx-auto">
                    Define appointment categories so the AI understands what your
                    practice offers and how long each visit takes.
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8 text-xs border-brand text-brand hover:bg-brand hover:text-white"
                    onClick={handleAdd}
                  >
                    <Plus className="mr-1.5 h-3.5 w-3.5" />
                    Add Your First Appointment Category
                  </Button>
                </div>
              ) : (
                <div className="space-y-2">
                  {appointmentCategories.map((category) => (
                    <div
                      key={category.id}
                      className="flex items-start justify-between p-3 bg-gray-50 rounded-lg border border-gray-200"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-0.5">
                          <p className="text-sm font-medium text-gray-900">
                            {category.name}
                          </p>
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-brand/10 text-brand">
                            <Clock className="h-3 w-3" />
                            {formatDuration(category.duration_minutes)}
                          </span>
                        </div>
                        <p className="text-xs text-gray-600 mt-0.5 leading-relaxed">
                          {category.description}
                        </p>
                      </div>
                      <div className="flex items-center space-x-1 ml-3 flex-shrink-0">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEdit(category)}
                          className="h-7 w-7 p-0 text-brand hover:text-brand-hover hover:bg-brand/10"
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteClick(category)}
                          className="h-7 w-7 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {editingCategory
                ? "Edit Appointment Category"
                : "Add Appointment Category"}
            </DialogTitle>
            <DialogDescription>
              {editingCategory
                ? "Update the appointment category details below."
                : "Define a new appointment category. The AI will use this information to determine how long to block on the calendar when scheduling a patient."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-5 py-4">
            {/* Name */}
            <div className="space-y-2">
              <label
                htmlFor="type-name"
                className="text-sm font-medium text-gray-700"
              >
                Appointment Name
              </label>
              <Input
                id="type-name"
                type="text"
                placeholder="e.g., New Patient Consultation"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="h-11"
                disabled={saving}
              />
            </div>

            {/* Description */}
            <div className="space-y-2">
              <label
                htmlFor="type-description"
                className="text-sm font-medium text-gray-700"
              >
                Description
              </label>
              <textarea
                id="type-description"
                placeholder="e.g., First-time introductory appointment with the doctor. Includes medical history review and initial assessment."
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="flex w-full rounded-md border border-input bg-background px-3 py-2.5 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 min-h-[80px] resize-none"
                disabled={saving}
                rows={3}
              />
              <p className="text-xs text-gray-500">
                Help the AI understand what this appointment involves so it can
                match patients to the right type.
              </p>
            </div>

            {/* Duration */}
            <div className="space-y-2">
              <label
                htmlFor="type-duration"
                className="text-sm font-medium text-gray-700"
              >
                Duration
              </label>
              <select
                id="type-duration"
                value={durationMinutes}
                onChange={(e) => setDurationMinutes(Number(e.target.value))}
                className="flex h-11 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                disabled={saving}
              >
                {DURATION_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
              <p className="text-xs text-gray-500">
                The AI will look for an available time slot of this length when
                scheduling this type of appointment.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowDialog(false);
                setEditingCategory(null);
                setName("");
                setDescription("");
                setDurationMinutes(30);
              }}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving || !name.trim() || !description.trim()}
              className="bg-brand hover:bg-brand-hover text-white"
            >
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {editingCategory ? "Updating..." : "Adding..."}
                </>
              ) : editingCategory ? (
                "Update Category"
              ) : (
                "Add Category"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Appointment Category</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "
              {categoryToDelete?.name || "this appointment category"}"? This action
              cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setShowDeleteDialog(false);
                setCategoryToDelete(null);
              }}
              disabled={deleting}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={handleDelete}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {deleting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default AppointmentCategoriesPage;
